import {
  OPT_IN_PREFERENCES,
  OptInPreference,
} from '@/constants/features/opt-in';
import {
  ALL_OR_NOTHING_GRANTS,
  AllOrNothingGroupId,
  CONSENT_CHECKBOXES,
  ConsentCheckboxId,
} from '../config/checkboxes';

// A bucket is one kind of data collection the user can consent to: clickstream
// (referred to as cs, the master consent every other bucket depends on), search,
// e-commerce, ads and ai. This file answers two questions about a consent
// screen: which buckets it is allowed to touch, and which a given set of ticks
// actually grants.

// Dedupes and puts buckets in one canonical order, so two equivalent writes
// compare equal. A bucket this build doesn't recognise is kept rather than
// dropped: the stored list is shared with the extension and the server, which
// may know buckets this build doesn't.
export const normalizePreferences = (
  preferences: OptInPreference[]
): OptInPreference[] => {
  const uniquePreferences = new Set(preferences);
  const recognized = OPT_IN_PREFERENCES.filter(preference =>
    uniquePreferences.has(preference)
  );
  const unrecognized = Array.from(uniquePreferences).filter(
    preference => !recognized.includes(preference)
  );
  return [...recognized, ...unrecognized];
};

// The all-or-nothing groups with at least one checkbox on this screen.
const visibleAllOrNothingGroups = (
  visible: ConsentCheckboxId[]
): AllOrNothingGroupId[] => {
  const ids = visible
    .map(checkboxId => CONSENT_CHECKBOXES[checkboxId].allOrNothingGroup)
    .filter((id): id is AllOrNothingGroupId => id !== undefined);
  return Array.from(new Set(ids));
};

export const groupCheckboxes = (
  groupId: AllOrNothingGroupId,
  visible: ConsentCheckboxId[]
): ConsentCheckboxId[] =>
  visible.filter(
    checkboxId => CONSENT_CHECKBOXES[checkboxId].allOrNothingGroup === groupId
  );

// Every bucket this screen is allowed to write. Callers use it as a fence: a
// bucket outside this set is left exactly as the user already had it. An
// all-or-nothing group counts as soon as it is on screen, because the question
// here is what the screen could grant, not what is ticked right now.
export const grantableBuckets = (
  visible: ConsentCheckboxId[]
): OptInPreference[] =>
  normalizePreferences([
    ...visible.flatMap(checkboxId => CONSENT_CHECKBOXES[checkboxId].grants),
    ...visibleAllOrNothingGroups(visible).flatMap(
      groupId => ALL_OR_NOTHING_GRANTS[groupId]
    ),
  ]);

// What this particular set of ticks grants. An ordinary checkbox grants on its
// own; an all-or-nothing group grants only once every one of its visible members
// is ticked.
export const grantedBuckets = (
  checked: ConsentCheckboxId[],
  visible: ConsentCheckboxId[]
): OptInPreference[] =>
  normalizePreferences([
    ...checked.flatMap(checkboxId => CONSENT_CHECKBOXES[checkboxId].grants),
    ...visibleAllOrNothingGroups(visible)
      .filter(groupId =>
        groupCheckboxes(groupId, visible).every(checkboxId =>
          checked.includes(checkboxId)
        )
      )
      .flatMap(groupId => ALL_OR_NOTHING_GRANTS[groupId]),
  ]);
